import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import Modal from "@/components/controls/Modal";
import SingleSelect, {
  SingleSelectOption
} from "@/components/controls/SingleSelect";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { FormProductMap } from "@/entities/formProductMap/types";
import { Product } from "@/pages/settings/components/settingsItems/hooks/types";
import {
  HUBSPOT_SETTINGS_TRANSLATION_PREFIX,
  MAX_PRODUCT_RULES,
  ProductRuleRow,
  productMapErrors,
  productMapFromRows,
  productRuleRows
} from "@/pages/settings/components/integrations/hubspot/hubspotSettingsOptions";

const PREFIX = `${HUBSPOT_SETTINGS_TRANSLATION_PREFIX}.productMap`;

const SELECT_CLASSNAME = "text-start border border-border-border2 pl-0";
const SELECT_TRIGGER_CLASSNAME = "w-full min-h-8 h-9";

type KeyedRow = ProductRuleRow & { key: number };

interface FormProductMapDialogProps {
  productMap: FormProductMap;
  products: Product[];
  isCatalogUnavailable: boolean;
  isSaving: boolean;
  onSave: (productMap: FormProductMap) => void;
  onClose: () => void;
}

// Mounted per open, so the draft always starts from the saved map.
export const FormProductMapDialog: React.FC<FormProductMapDialogProps> = ({
  productMap,
  products,
  isCatalogUnavailable,
  isSaving,
  onSave,
  onClose
}) => {
  const { t } = useTranslation();
  // Each row keeps its key when moved, so focus follows the rule it was on.
  const nextKey = useRef(0);
  const keyed = (row: ProductRuleRow): KeyedRow => ({
    ...row,
    key: nextKey.current++
  });
  const [rows, setRows] = useState<KeyedRow[]>(() =>
    productRuleRows(productMap).map(keyed)
  );

  const catalogIds = new Set(products.map((product) => product.id));
  const catalogOptions: SingleSelectOption[] = products.map((product) => ({
    id: product.id,
    displayName: product.name
  }));
  // A rule whose product has left the catalog keeps it selected, marked, so
  // the admin can see what to repoint.
  const optionsFor = (productId: string | null): SingleSelectOption[] =>
    !productId || catalogIds.has(productId) || isCatalogUnavailable
      ? catalogOptions
      : [
          ...catalogOptions,
          { id: productId, displayName: t(`${PREFIX}.notInCatalog`) }
        ];
  const errors = isCatalogUnavailable ? [] : productMapErrors(rows, catalogIds);

  const updateRow = (index: number, changes: Partial<ProductRuleRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );

  const moveRow = (index: number, offset: -1 | 1) =>
    setRows((current) => {
      const next = [...current];
      [next[index], next[index + offset]] = [next[index + offset], next[index]];
      return next;
    });

  return (
    <Modal
      open
      setIsOpen={(open) => {
        if (!open && !isSaving) onClose();
      }}
      contentClassname="w-[45rem]"
      contentContainerClassname="max-h-[70vh] overflow-y-auto"
      data-test="form-product-map-dialog"
      title={
        <Text as="span" variant="lgSubtitleH1">
          {t(`${PREFIX}.title`)}
        </Text>
      }
      content={
        <div className="flex flex-col gap-3 px-6 py-4">
          <Text variant="bodySecondary">{t(`${PREFIX}.dialogSubtitle`)}</Text>
          {rows.map((row, index) => (
            <div
              key={row.key}
              className="flex items-center gap-2"
              data-test={`form-product-rule-${index}`}
            >
              <Input
                aria-label={t(`${PREFIX}.a11y.ruleValues`, {
                  position: index + 1
                })}
                placeholder={t(`${PREFIX}.valuesPlaceholder`)}
                value={row.valuesText}
                onChange={(event) =>
                  updateRow(index, { valuesText: event.target.value })
                }
                disabled={isSaving}
                containerClassName="flex-1"
              />
              <div className="w-[14rem]">
                <SingleSelect
                  fieldName={`product-${index}`}
                  options={optionsFor(row.productId)}
                  selectedValue={row.productId ?? undefined}
                  onSelection={(value) =>
                    updateRow(index, { productId: String(value) })
                  }
                  formatter={(value) =>
                    optionsFor(row.productId).find(
                      (option) => option.id === value
                    )?.displayName ?? String(value)
                  }
                  placeholder={t(`${PREFIX}.productPlaceholder`)}
                  disabled={isSaving}
                  isLoading={isCatalogUnavailable}
                  className={SELECT_CLASSNAME}
                  popoverTriggerClassName={SELECT_TRIGGER_CLASSNAME}
                  variant="form"
                />
              </div>
              <IconButton
                variant="iconNoBg"
                size="lg"
                aria-label={t(`${PREFIX}.a11y.moveRuleUp`, {
                  position: index + 1
                })}
                onClick={() => moveRow(index, -1)}
                disabled={isSaving || index === 0}
              >
                <ArrowUp />
              </IconButton>
              <IconButton
                variant="iconNoBg"
                size="lg"
                aria-label={t(`${PREFIX}.a11y.moveRuleDown`, {
                  position: index + 1
                })}
                onClick={() => moveRow(index, 1)}
                disabled={isSaving || index === rows.length - 1}
              >
                <ArrowDown />
              </IconButton>
              <IconButton
                variant="iconNoBg"
                size="lg"
                aria-label={t(`${PREFIX}.a11y.removeRule`, {
                  position: index + 1
                })}
                onClick={() =>
                  setRows((current) => current.filter((_, i) => i !== index))
                }
                disabled={isSaving}
              >
                <Trash2 />
              </IconButton>
            </div>
          ))}
          <Button
            variant="tertiary"
            className="w-fit"
            onClick={() =>
              setRows((current) => [
                ...current,
                keyed({ valuesText: "", productId: null })
              ])
            }
            disabled={isSaving || rows.length >= MAX_PRODUCT_RULES}
          >
            {t(`${PREFIX}.add`)}
          </Button>
          {errors.length > 0 && (
            <div
              className="flex flex-col gap-1"
              data-test="form-product-map-errors"
            >
              {errors.map((error) => (
                <Text key={error} variant="smBodyError">
                  {t(`${PREFIX}.errors.${error}`)}
                </Text>
              ))}
            </div>
          )}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t("cancel")}
          </Button>
          <Button
            variant="primary"
            data-test="form-product-map-save"
            onClick={() => onSave(productMapFromRows(rows))}
            disabled={isSaving || isCatalogUnavailable || errors.length > 0}
          >
            {t("save")}
          </Button>
        </div>
      }
    />
  );
};
